import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import useConsultantBookingsApi from '../useConsultantBookingsApi';
import consultantActions from '../../actions/consultantActions';
import useDays from '../useDays';
import useGroupedTimeslots from '../useGroupedTimeslots';
import useBookingEngine from '../useBookingEngine';
import getValidSlots from '../../utils/getValidSlots';
import getValidClinicDays from '../../utils/getValidClinicDays';

export default (
  gmcCode,
  numberOfDays = 9,
  isMonthView = false,
  currentSelectedDate = null,
  externalSelectedSlot = null,
  selectedHospitals = [],
) => {
  const consultantBookings = useConsultantBookingsApi();
  const bookingEngine = useBookingEngine();
  const { fetchConsultantClinicDays, fetchConsultantSlots } =
    consultantActions(consultantBookings, bookingEngine);
  const { clinicDays, slots, hospitals, consultants } = useSelector(
    ({ consultants }) => consultants,
  );

  const validSlots = useMemo(() => {
    return getValidSlots(slots)
  }, [slots]);

  const slotsForSelectedDate = useMemo(() => {
    if(!currentSelectedDate) return [];
    const selectedDateString = moment(currentSelectedDate).format('YYYY-MM-DD');
    return validSlots.filter(slots => slots.slotDate === selectedDateString);
  }, [validSlots, currentSelectedDate])

  const validClinicDays = useMemo(() => {
    return getValidClinicDays(validSlots, clinicDays);
  }, [validSlots, clinicDays])

  const clinicDaysMap = useMemo(
    () =>
      validClinicDays.reduce(
        (days, { date, ...day }) => ({
          ...days,
          [date]: day,
        }),
        {},
      ),
    [validClinicDays],
  );

  const nextBusinessDay = useMemo(() => {
    const tomorrow = moment().startOf('day').add(1, 'day');

    let increment;
    switch (tomorrow.day()) {
      case 6:
        increment = 2;
        break;
      case 0:
        increment = 1;
        break;
      default:
        increment = 0;
    }

    return tomorrow.add(increment, 'days');
  }, []);

  const [selectedDay, selectableDays, actions, setFirstDay] = useDays(numberOfDays, {
    startDate: moment(),
    endDate: moment().startOf('day').add(90, 'days'),
    extraData: date => ({
      clinic: clinicDaysMap[date.format('YYYY-MM-DD')],
    }),
    isDisabled: ({ clinic }) => !clinic,
    dependencies: [gmcCode, clinicDaysMap],
  });

  useEffect(() => {
    const firstClinicDay = Object.keys(clinicDaysMap).find(element => moment(element).diff(nextBusinessDay) >= 0);
    if (firstClinicDay) {
      setFirstDay(moment(firstClinicDay));
    }
  }, [clinicDaysMap]);

  useEffect(() => {
    if (currentSelectedDate) {
      const selectedMoment = moment(currentSelectedDate);
      if (isMonthView) {
        setFirstDay(selectedMoment.clone().startOf('month'));
      } else {
        // For week view, show the week that contains the selected date
        const startOfSelectedWeek = selectedMoment.clone().startOf('week');
        const weekStartDay = startOfSelectedWeek.clone().subtract(1, 'days');

        const adjustedStartDay = weekStartDay.isBefore(nextBusinessDay)
          ? nextBusinessDay
          : weekStartDay;
        setFirstDay(adjustedStartDay);
      }
    } else if (isMonthView) {
      const currentMonth = moment().startOf('month');
      setFirstDay(currentMonth);
    } else {
      setFirstDay(nextBusinessDay);
    }
  }, [isMonthView, nextBusinessDay, setFirstDay, currentSelectedDate]);

  const [loaded, setLoaded] = useState(false);
  const [bookableSlots, setBookableSlots] = useState(false);
  useEffect(() => {
    fetchConsultantClinicDays({
      gmcCode,
      span: 90,
      fromDate: moment().format('YYYY-MM-DD'),
    }).then(() => {
      setLoaded(true);
    });
  }, [gmcCode, nextBusinessDay]);

  useEffect(() => {
    if (clinicDays.length == 0) {
      setBookableSlots(false);
    } else if (clinicDays.length >= 1) {
      setBookableSlots(true);
    }
    fetchConsultantSlots({
      gmcCode,
      fromDate: moment().format('YYYY-MM-DD'),
      locations: selectedHospitals,
    })
  }, [gmcCode, selectedHospitals, clinicDays.length]);

  const timeslots = useMemo(
    () =>
      slotsForSelectedDate.reduce((_timeslots, { slotTime, hospitalId, ...timeslot }) => {
        const newTimeslots = { ..._timeslots };
        const mSlotTime = moment(slotTime, 'HH.mm');
        const slot = mSlotTime.format('HH:mm');

        if (!newTimeslots.hasOwnProperty(slot)) {
          newTimeslots[slot] = [];
        }

        newTimeslots[slot].push({
          consultant: {
            gmcCode,
            ...consultants[gmcCode],
          },
          hospital: {
            hospitalId,
            ...hospitals[hospitalId],
          },
          slotTime,
          ...timeslot,
          type: 'CONSULTANT',
        });

        return newTimeslots;
      }, {}),
    [slotsForSelectedDate, consultants, hospitals, gmcCode],
  );

  const sortedTimeslots = useMemo(() => Object.entries(timeslots).sort(), [timeslots]);
  const [selectedSlot, groupedTimeslots, persistedSelectedSlot] = useGroupedTimeslots(
    sortedTimeslots,
    [],
    externalSelectedSlot,
  );

  // Use external selected slot as fallback when internal selection is lost (Safari fix)
  const effectiveSelectedSlot =
    selectedSlot ||
    (persistedSelectedSlot
      ? sortedTimeslots.find(([time]) => time === persistedSelectedSlot)
      : null);

  const selectedSlotsArray = effectiveSelectedSlot ? effectiveSelectedSlot[1] : [];

  return {
    loaded,
    bookableSlots,
    slots: groupedTimeslots,
    selectedSlots: selectedSlotsArray,
    clinicDays: {
      selectedDay,
      selectableDays,
      actions,
    },
    hospitals: Object.entries(hospitals),
  };
};
